import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, onSnapshot, query, deleteDoc, doc, updateDoc, orderBy } from 'firebase/firestore';
import { FaTrash, FaCheck, FaTimes, FaSearch, FaEnvelope, FaPhone, FaUser, FaClock } from 'react-icons/fa';
import ConfirmModal from '../ConfirmModal';

const SupportModule = ({ selectedCountry }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [confirmConfig, setConfirmConfig] = useState({ isOpen: false, title: '', message: '', type: 'danger', singleButton: false, onConfirm: null });
    const [confirmModalData, setConfirmModalData] = useState({ isOpen: false, ticketId: null }); // Legacy for deletion

    useEffect(() => {
        const q = query(collection(db, 'support_tickets'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                date: doc.data().createdAt
                    ? new Date(doc.data().createdAt.seconds * 1000).toLocaleString()
                    : 'N/A'
            }));
            setTickets(data);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleDeleteTicket = async (id) => {
        try {
            await deleteDoc(doc(db, 'support_tickets', id));
        } catch (err) {
            console.error("Error deleting ticket:", err);
        } finally {
            setConfirmModalData({ isOpen: false, ticketId: null });
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
        try {
            await updateDoc(doc(db, 'support_tickets', id), {
                status: newStatus
            });
            setConfirmConfig({
                isOpen: true,
                title: "Status Updated",
                message: `Ticket status has been set to ${newStatus}.`,
                type: "success",
                singleButton: true
            });
        } catch (err) {
            console.error("Error updating status:", err);
            setConfirmConfig({
                isOpen: true,
                title: "Update Failed",
                message: "Failed to update ticket status: " + err.message,
                type: "danger",
                singleButton: true
            });
        }
    };

    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch =
            (ticket.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (ticket.userName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (ticket.userEmail || '').toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'All' || ticket.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    return (
        <div className="content-section fade-in">
            <div className="content-header">
                <div>
                    <h2 className="content-title">Support Tickets</h2>
                    <p style={{ margin: 0, color: 'var(--text-tertiary)', fontSize: '14px' }}>
                        Manage user inquiries and technical support requests.
                    </p>
                </div>
            </div>

            <div className="dashboard-controls" style={{ marginBottom: '24px' }}>
                <div className="search-bar-mini" style={{ width: '100%' }}>
                    <FaSearch className="search-icon-mini" />
                    <input
                        type="text"
                        placeholder="Search by title, name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <ConfirmModal
                    isOpen={confirmConfig.isOpen}
                    onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
                    onConfirm={confirmConfig.onConfirm}
                    title={confirmConfig.title}
                    message={confirmConfig.message}
                    type={confirmConfig.type}
                    singleButton={confirmConfig.singleButton}
                    confirmText={confirmConfig.singleButton ? "Got it" : "Confirm"}
                />
            </div>

            {loading ? (
                <div className="loading-state">Fetching support tickets...</div>
            ) : (
                <div className="tickets-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '20px' }}>
                    {filteredTickets.length > 0 ? (
                        filteredTickets.map(ticket => (
                            <div key={ticket.id} className="ticket-card" style={{
                                background: 'var(--bg-card)',
                                padding: '24px',
                                borderRadius: '16px',
                                border: '1px solid var(--border-light)',
                                transition: 'all 0.3s ease',
                                position: 'relative'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                    <div>
                                        <span className={`status-badge ${ticket.status}`} style={{
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            display: 'inline-block',
                                            marginBottom: '8px',
                                            background: ticket.status === 'open' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                                            color: ticket.status === 'open' ? '#3B82F6' : '#10B981'
                                        }}>
                                            {ticket.status}
                                        </span>
                                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{ticket.title}</h3>
                                    </div>
                                    <button className="btn-action delete" onClick={() => setConfirmModalData({ isOpen: true, ticketId: ticket.id })} style={{ color: '#EF4444' }}><FaTrash /></button>
                                </div>

                                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '20px' }}>
                                    {ticket.description}
                                </p>

                                <div className="ticket-meta" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', paddingTop: '16px', borderTop: '1px solid var(--border-light)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
                                        <FaUser size={12} /> {ticket.userName || 'Unknown'}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
                                        <FaClock size={12} /> {ticket.date}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
                                        <FaEnvelope size={12} /> {ticket.userEmail || 'N/A'}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
                                        <FaPhone size={12} /> {ticket.userPhone || 'N/A'}
                                    </div>
                                </div>

                                <div className="ticket-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                                    {ticket.status === 'open' ? (
                                        <button className="btn-save" onClick={() => handleUpdateStatus(ticket.id, 'resolved')} style={{ width: '100%', fontSize: '13px' }}>
                                            <FaCheck style={{ marginRight: '8px' }} /> Mark as Resolved
                                        </button>
                                    ) : (
                                        <button className="btn-cancel" onClick={() => handleUpdateStatus(ticket.id, 'open')} style={{ width: '100%', fontSize: '13px' }}>
                                            <FaTimes style={{ marginRight: '8px' }} /> Re-open Ticket
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{ textAlign: 'center', padding: '60px', gridColumn: '1 / -1', color: 'var(--text-tertiary)' }}>
                            No support tickets found matching your filters.
                        </div>
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={confirmModalData.isOpen}
                onClose={() => setConfirmModalData({ isOpen: false, ticketId: null })}
                onConfirm={() => handleDeleteTicket(confirmModalData.ticketId)}
                title="Delete Support Ticket?"
                message="Are you sure you want to permanently delete this support ticket? This action cannot be undone."
                confirmText="Yes, Delete Ticket"
                type="danger"
            />
        </div>
    );
};

export default SupportModule;
